// API Response Handler
// Standardized handling of API responses with consistent structure

/**
 * Standard API Response Structure
 * {
 *   "error": false,
 *   "message": "success",
 *   "result": {
 *     "docs": [...], // Array of data
 *     "totalDocs": 0,
 *     "offset": 0,
 *     "limit": 10,
 *     "totalPages": 1,
 *     "page": 1,
 *     "pagingCounter": 1,
 *     "hasPrevPage": false,
 *     "hasNextPage": false,
 *     "prevPage": null,
 *     "nextPage": null
 *   }
 * }
 */

/**
 * Process API response and extract data with pagination info
 * @param {Object} response - Raw API response
 * @param {string} dataKey - Key to extract from result (e.g., 'docs', 'doctors', 'clinics')
 * @returns {Object} - Processed response with data and pagination
 */
export const processApiResponse = (response, dataKey = 'docs') => {
  try {
    // Handle different response structures
    if (!response) {
      return {
        data: [],
        pagination: null,
        error: true,
        message: 'No response received'
      };
    }

    // Check if response has error
    if (response.error) {
      return {
        data: [],
        pagination: null,
        error: true,
        message: response.message || 'API returned an error'
      };
    }

    // Extract data from result
    const result = response.result || {};
    const data = result[dataKey] || result.docs || result.doctors || result.bookings || result.clinics || [];
    
    // Extract pagination info
    const pagination = {
      totalDocs: result.totalDocs || 0,
      offset: result.offset || 0,
      limit: result.limit || 10,
      totalPages: result.totalPages || 1,
      page: result.page || 1,
      pagingCounter: result.pagingCounter || 1,
      hasPrevPage: result.hasPrevPage || false,
      hasNextPage: result.hasNextPage || false,
      prevPage: result.prevPage || null,
      nextPage: result.nextPage || null
    };

    return {
      data: Array.isArray(data) ? data : [],
      pagination,
      error: false,
      message: response.message || 'Success'
    };
  } catch (error) {
    console.error('Error processing API response:', error);
    return {
      data: [],
      pagination: null,
      error: true,
      message: 'Failed to process API response'
    };
  }
};

/**
 * Create standardized error response
 * @param {Error} error - Error object
 * @param {string} operation - Operation that failed
 * @returns {Object} - Standardized error response
 */
export const createErrorResponse = (error, operation = 'API call') => {
  console.error(`${operation} error:`, error);
  
  return {
    data: [],
    pagination: null,
    error: true,
    message: error.message || `Failed to ${operation.toLowerCase()}`
  };
};

/**
 * Handle API request with standardized response processing
 * @param {Function} apiCall - Function that makes the API call
 * @param {string} dataKey - Key to extract from result
 * @param {string} operation - Operation name for error handling
 * @returns {Promise<Object>} - Processed response
 */
export const handleApiRequest = async (apiCall, dataKey = 'docs', operation = 'API call') => {
  try {
    const response = await apiCall();
    return processApiResponse(response, dataKey);
  } catch (error) {
    return createErrorResponse(error, operation);
  }
};

/**
 * Build query parameters from filters object
 * @param {Object} filters - Filters object
 * @returns {URLSearchParams} - Query parameters
 */
export const buildQueryParams = (filters = {}) => {
  const queryParams = new URLSearchParams();
  
  Object.keys(filters).forEach(key => {
    if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
      queryParams.append(key, filters[key]);
    }
  });
  
  return queryParams;
};

/**
 * Create API request headers with authentication
 * @param {string} token - Authentication token
 * @param {Object} additionalHeaders - Additional headers
 * @returns {Object} - Headers object
 */
export const createApiHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...additionalHeaders
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

/**
 * Make API request with standardized error handling
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} - API response
 */
export const makeApiRequest = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to server');
    }
    throw error;
  }
};
